import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroFallback.css';

/**
 * HeroFallback Component
 * Displays when the hero video fails to load
 * Features a luxurious editorial layout with product images
 */
const HeroFallback = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [logoImage, setLogoImage] = useState('');

  // Fade-in animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Load logo
  useEffect(() => {
    const loadLogo = () => {
      const logoUrl = 'https://images.vastrebyvijh.com/vastre-images/logo/desktop/2025-08-26/logo_desktop_1756240073154_0_1080w.webp';
      const img = new Image();
      img.onload = () => {
        setLogoImage(logoUrl);
      };
      img.onerror = () => {
        setLogoImage('/images/logo/hero_desktop_1756135482771_0_1080w.webp');
      };
      img.src = logoUrl;
    };
    loadLogo();
  }, []);

  // Product images for fallback display
  const fallbackImages = [
    {
      url: 'https://images.vastrebyvijh.com/vastre-images/products/premium_brocade_kurta_ensemble_with_tailored_jacket___pants_1757000178721/image_8_1440w.webp',
      alt: 'Premium Brocade Kurta Ensemble'
    },
    {
      url: 'https://images.vastrebyvijh.com/vastre-images/products/regal_black_textured_jodhpuri_bandhgala_suit_1757006680315/image_2_1440w.webp',
      alt: 'Regal Black Jodhpuri Bandhgala Suit'
    },
    {
      url: 'https://images.vastrebyvijh.com/vastre-images/products/premium_satin_striped_double_breasted_party_shirt_with_lapels_1757006220471/image_1_1440w.webp',
      alt: 'Premium Satin Striped Party Shirt'
    }
  ];

  // Smooth scroll to next section
  const handleScrollDown = () => {
    const nextSection = document.querySelector('.signature-section, .collections-section, .elegant-about-section');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Navigate to collections
  const handleExploreClick = () => {
    navigate('/shop');
  };

  return (
    <section className={`herofallback-section ${isVisible ? 'herofallback-visible' : ''}`}>
      {/* Image Collage Background */}
      <div className="herofallback-image-container">
        {/* Image 1 - Left */}
        <div className="herofallback-image herofallback-image-1">
          <img 
            src={fallbackImages[0].url} 
            alt={fallbackImages[0].alt}
            loading="eager"
            fetchpriority="high"
          />
        </div>

        {/* Image 2 - Center */}
        <div className="herofallback-image herofallback-image-2">
          <img 
            src={fallbackImages[1].url} 
            alt={fallbackImages[1].alt}
            loading="eager"
            fetchpriority="high"
          />
        </div>

        {/* Image 3 - Right */}
        <div className="herofallback-image herofallback-image-3">
          <img 
            src={fallbackImages[2].url} 
            alt={fallbackImages[2].alt}
            loading="eager"
            fetchpriority="high"
          />
        </div>

        {/* Gradient Overlays for Blending */}
        <div className="herofallback-gradient-left"></div>
        <div className="herofallback-gradient-right"></div>
        <div className="herofallback-gradient-center"></div>

        {/* Dark Burgundy Overlay for Unity */}
        <div className="herofallback-overlay"></div>

        {/* Vignette Effect */}
        <div className="herofallback-vignette"></div>

        {/* Grain Texture Overlay */}
        <div className="herofallback-grain"></div>
      </div>

      {/* Content Overlay */}
      <div className="herofallback-content">
        <div className="herofallback-text-container">
          {/* Brand Name Text */}
          <h1 className="herofallback-brand-title">
            Vastré By Vijh
          </h1>

          {/* Tagline */}
          <p className="herofallback-tagline">
            Crafted for Rarity
          </p>

          {/* CTA Button */}
          <button 
            className="herofallback-cta"
            onClick={handleExploreClick}
            aria-label="Explore the Collection"
          >
            <span className="herofallback-cta-text">Explore the Collection</span>
            <span className="herofallback-cta-arrow">→</span>
          </button>
        </div>

        {/* Scroll Indicator */}
        <div 
          className="herofallback-scroll-indicator"
          onClick={handleScrollDown}
          role="button"
          tabIndex={0}
          aria-label="Scroll down"
        >
          <div className="herofallback-scroll-chevron">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 14L12 19L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 5L12 10L17 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="herofallback-scroll-text">Scroll to explore</span>
        </div>
      </div>
    </section>
  );
};

export default HeroFallback;

